import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Switch,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EvStationIcon from '@mui/icons-material/EvStation';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { useMaterialUIController } from 'context';
import { useSnackbar } from 'notistack';
import useApiRequest from 'utils/Apirequest';

function ChargerOperationalTimingsModal({ open, onClose, locationId, onSave }) {
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const apiRequest = useApiRequest();

  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [chargerTimings, setChargerTimings] = useState({});
  const isEndTimeValid = (startTime, endTime) => {
    if (!startTime || !endTime) return true;
    return endTime > startTime; // works for HH:mm format
  };
  useEffect(() => {
    if (open && locationId) {
      fetchLocationChargers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId]);
  const MAX_SLOTS_PER_CHARGER = 5; // set your limit here

  const fetchLocationChargers = async () => {
    try {
      setLoading(true);
      const { data } = await apiRequest('get', `charger-operational-timings/all`, null, {}, {});

      if (data?.success) {
        const location = data.data.find(loc => loc.locationId === locationId);
        if (location) {
          setLocationData(location);

          // Initialize charger timings state
          const timingsMap = {};
          location.chargers.forEach(charger => {
            timingsMap[charger.chargerId] = charger.operationalTimings || [];
          });
          setChargerTimings(timingsMap);
        }
      }
    } catch (error) {
      console.error('Error fetching chargers:', error);
      enqueueSnackbar('Failed to load chargers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeSlot = (chargerId) => {
    setChargerTimings(prev => {
      const existingSlots = prev[chargerId] || [];

      // ❌ Block if max reached
      if (existingSlots.length >= MAX_SLOTS_PER_CHARGER) {
        enqueueSnackbar(
          `Maximum ${MAX_SLOTS_PER_CHARGER} time slots allowed per charger.`,
          { variant: 'warning' }
        );
        return prev;
      }

      // Allow add
      return {
        ...prev,
        [chargerId]: [
          ...existingSlots,
          {
            enabled: true,
            startTime: '',
            endTime: '',
            isDraft: true,
          },
        ],
      };
    });
  };


  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const normalizeRange = (start, end) => {
    const startMin = timeToMinutes(start);
    let endMin = timeToMinutes(end);

    // Overnight slot (crosses midnight)
    if (endMin <= startMin) {
      endMin += 24 * 60;
    }

    return [startMin, endMin];
  };
  const validateSlot = (slots, index, candidate) => {
    if (!candidate.startTime || !candidate.endTime) return true;

    const others = slots.filter((_, i) => i !== index);
    return !isTimeOverlap(others, candidate);
  };

  const isTimeOverlap = (existingSlots, newSlot) => {
    const [newStart, newEnd] = normalizeRange(
      newSlot.startTime,
      newSlot.endTime
    );

    return existingSlots.some(slot => {
      if (!slot.enabled || !slot.startTime || !slot.endTime) return false;

      const [existingStart, existingEnd] = normalizeRange(
        slot.startTime,
        slot.endTime
      );

      return newStart < existingEnd && newEnd > existingStart;
    });
  };

  const handleRemoveTimeSlot = (chargerId, index) => {
    setChargerTimings(prev => ({
      ...prev,
      [chargerId]: prev[chargerId].filter((_, i) => i !== index)
    }));
  };

  const handleTimeSlotChange = (chargerId, index, field, value) => {
    const slots = chargerTimings[chargerId] || [];
    const candidate = {
      ...slots[index],
      [field]: value,
    };

    // ❌ BLOCK update BEFORE setState
    if (!validateSlot(slots, index, candidate)) {
      enqueueSnackbar(
        'Time slot overlaps with an existing slot.',
        { variant: 'error' }
      );
      return; // ⛔ hard stop — no state update
    }

    //  SAFE update
    setChargerTimings(prev => {
      const updated = [...prev[chargerId]];
      updated[index] = {
        ...candidate,
        isDraft: false,
      };

      return {
        ...prev,
        [chargerId]: updated,
      };
    });
  };

  const hasInvalidSlots = Object.values(chargerTimings).some(slots =>
    slots.some(slot => !slot.startTime || !slot.endTime)
  );

  const handleSubmit = async () => {
    try {
      // Prepare bulk update payload with hardcoded timezone
      const chargerTimingsArray = Object.entries(chargerTimings).map(([chargerId, timings]) => ({
        chargerId,
        operationalTimings: timings.map(slot => ({
          ...slot,
          timezone: 'Asia/Dubai'
        }))
      }));

      const { data } = await apiRequest(
        'put',
        `charger-operational-timings/${locationId}/bulk`,
        { chargerTimings: chargerTimingsArray },
        {},
        {}
      );

      if (data?.success) {
        enqueueSnackbar('Charger operational timings updated successfully', { variant: 'success' });
        if (onSave) onSave();
        handleClose();
      } else {
        enqueueSnackbar(data?.message || 'Failed to update timings', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error saving timings:', error);
      enqueueSnackbar('Failed to save configuration', { variant: 'error' });
    }
  };

  const handleClose = () => {
    setLocationData(null);
    setChargerTimings({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        style={
          darkMode
            ? { backgroundColor: '#202940', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
            : { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
        }
      >
        <MDBox display="flex" alignItems="center">
          <EvStationIcon sx={{ mr: 1 }} />
          <MDTypography variant="h5" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
            Charger Operational Timings - {locationData?.locationName}
          </MDTypography>
        </MDBox>
        <IconButton aria-label="close" onClick={handleClose} style={darkMode ? { color: '#ffffff' } : {}}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={darkMode ? { backgroundColor: '#1a2035', color: '#ffffff', padding: '24px' } : { padding: '24px' }}>
        {loading ? (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="body2">Loading chargers...</MDTypography>
          </MDBox>
        ) : !locationData ? (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="body2">No location data found</MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {locationData.chargers.map((charger) => {
              const slotsCount = chargerTimings[charger.chargerId]?.length || 0;
              const isLimitReached = slotsCount >= MAX_SLOTS_PER_CHARGER;
              return (
                <Grid item xs={12} key={charger.chargerId}>
                  <Accordion
                    sx={{
                      backgroundColor: darkMode ? '#202940' : '#fff',
                      border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
                      boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&:hover': {
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                        }
                      }}
                    >
                      <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                        <MDBox display="flex" alignItems="center" gap={2}>
                          <EvStationIcon color="primary" fontSize="large" />
                          <MDBox>
                            <MDTypography variant="h6" fontWeight="bold" color={darkMode ? 'white' : 'dark'}>
                              {charger.chargerId}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" sx={{ display: 'block', mt: 0.3 }}>
                              {charger.chargerName}
                            </MDTypography>
                            <MDBox display="flex" gap={1} mt={0.5}>
                              <Chip
                                label={charger.type}
                                size="small"
                                color="info"
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={charger.status}
                                size="small"
                                color={charger.status === 'Active' ? 'success' : 'default'}
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                              />
                            </MDBox>
                          </MDBox>
                        </MDBox>
                        <Chip
                          label={`${slotsCount}/${MAX_SLOTS_PER_CHARGER} time slot(s)`}
                          color={isLimitReached ? 'warning' : 'primary'}
                          variant="outlined"
                          size="small"
                        />

                      </MDBox>
                    </AccordionSummary>

                    <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                      <MDBox>
                        {/* Time Slots - Card Layout */}
                        {chargerTimings[charger.chargerId]?.length > 0 ? (
                          <MDBox sx={{ mb: 2 }}>
                            {chargerTimings[charger.chargerId].map((slot, index) => (
                              <MDBox
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  p: 2,
                                  mb: 1.5,
                                  borderRadius: '8px',
                                  border: `1px solid ${darkMode ? '#344767' : '#e0e0e0'}`,
                                  backgroundColor: slot.enabled
                                    ? (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
                                    : (darkMode ? '#1a2035' : '#f9f9f9'),
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                                    borderColor: darkMode ? '#4CAF50' : '#4CAF50',
                                  }
                                }}
                              >
                                {/* Enable Switch */}
                                <MDBox
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    minWidth: '80px'
                                  }}
                                >
                                  <Switch
                                    checked={slot.enabled}
                                    onChange={(e) => handleTimeSlotChange(charger.chargerId, index, 'enabled', e.target.checked)}
                                    color="success"
                                  />
                                  <MDTypography variant="caption" color="text" fontSize="0.65rem" mt={0.5}>
                                    {slot.enabled ? 'Active' : 'Disabled'}
                                  </MDTypography>
                                </MDBox>

                                <Divider orientation="vertical" flexItem />

                              {/* Time Inputs */}
                              <MDBox sx={{ display: 'flex', gap: 2, flex: 1, alignItems: 'center' }}>
                                <MDBox sx={{ flex: 1 }}>
                                  <MDTypography variant="caption" color="text" fontWeight="medium" mb={0.5} display="block">
                                    Start Time
                                  </MDTypography>
                                  <MDInput
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) => {
                                      const newStartTime = e.target.value;

                                      // Reset end time if it becomes invalid
                                      if (slot.endTime && slot.endTime <= newStartTime) {
                                        handleTimeSlotChange(charger.chargerId, index, "startTime", newStartTime);
                                        handleTimeSlotChange(charger.chargerId, index, "endTime", "");
                                        enqueueSnackbar(
                                          "End time reset because it must be greater than start time",
                                          { variant: "warning" }
                                        );
                                        return;
                                      }

                                      handleTimeSlotChange(
                                        charger.chargerId,
                                        index,
                                        "startTime",
                                        newStartTime
                                      );
                                    }}
                                    disabled={!slot.enabled}
                                    fullWidth
                                  />

                                </MDBox>

                                  <MDBox
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      mt: 2.5
                                    }}
                                  >
                                    <MDTypography variant="h6" color="text">
                                      →
                                    </MDTypography>
                                  </MDBox>

                                <MDBox sx={{ flex: 1 }}>
                                  <MDTypography variant="caption" color="text" fontWeight="medium" mb={0.5} display="block">
                                    End Time
                                  </MDTypography>
                                  <MDInput
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) => {
                                      const newEndTime = e.target.value;

                                      if (!isEndTimeValid(slot.startTime, newEndTime)) {
                                        enqueueSnackbar(
                                          "End time must be greater than start time",
                                          { variant: "error" }
                                        );
                                        return;
                                      }

                                      handleTimeSlotChange(
                                        charger.chargerId,
                                        index,
                                        "endTime",
                                        newEndTime
                                      );
                                    }}
                                    disabled={!slot.enabled}
                                    fullWidth
                                    error={!isEndTimeValid(slot.startTime, slot.endTime)}
                                  />

                                </MDBox>
                              </MDBox>

                                <Divider orientation="vertical" flexItem />

                                {/* Delete Button */}
                                <MDBox>
                                  <IconButton
                                    onClick={() => handleRemoveTimeSlot(charger.chargerId, index)}
                                    color="error"
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </MDBox>
                              </MDBox>
                            ))}
                          </MDBox>
                        ) : (
                          <MDBox textAlign="center" py={2}>
                            <MDTypography variant="caption" color="text" sx={{ fontStyle: 'italic' }}>
                              No time slots configured for this charger
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Add Time Slot Button */}
                        <MDButton
                          variant="outlined"
                          color={sidenavColor}
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddTimeSlot(charger.chargerId)}
                          disabled={isLimitReached}
                          fullWidth
                        >
                          {isLimitReached
                            ? `Max ${MAX_SLOTS_PER_CHARGER} slots reached`
                            : 'Add Time Slot'}
                        </MDButton>
                      </MDBox>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        style={
          darkMode
            ? { backgroundColor: '#1a2035', justifyContent: 'flex-end', padding: '20px 24px', gap: '12px' }
            : { justifyContent: 'flex-end', padding: '20px 24px', gap: '12px' }
        }
      >
        <MDButton variant="outlined" color="secondary" onClick={handleClose} sx={{ minWidth: '100px' }}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color={sidenavColor}
          onClick={handleSubmit}
          disabled={loading || hasInvalidSlots}
        >
          Save All Timings
        </MDButton>

      </DialogActions>
    </Dialog>
  );
};

export default ChargerOperationalTimingsModal;
